import { action, makeObservable, observable } from 'mobx';

import Auth from './AuthManager';
import userManager from './UserManager';

import { DEFAULT_STUDENT_PROGRESS_TROPHY_TIERS, STUDENT_PROGRESS_WIDGET } from '../constants';

export class StudentProgressManager {
  entityId = '';

  classroomId = '';

  // raw data
  studentProgressData = undefined;

  @observable allowStudentProgressNavTab = true;

  /**
   * Widgets related to **Student Progress** allowed to be displayed on student dashboard.
   * Can be overridden via satellite index.js (or satelliteLayout.json if dynamic satellite)
   */
  @observable allowedStudentDashboardProgressWidgets = [
    STUDENT_PROGRESS_WIDGET.StudentProgressTrophiesWidget
  ];

  /**
   * Widgets related to **Student Progress** allowed to be displayed on ProgressView.
   *
   * This array determines which widgets are rendered and in what order. When populated,
   * it takes precedence over the prop-based system.
   *
   * **Precedence Logic:**
   * - If `allowedStudentProgressWidgets` is **empty/undefined**: Falls back to prop-based
   *   system where boolean props (e.g. `progressWidget`, `summaryWidget`,
   *   `trophiesWidget`, `weeklyPerformanceWidget`) are passed via `ProgressViewOverride`
   *   or `StudentHelperService.getProgressViewOverrideOptions()`
   * - If `allowedStudentProgressWidgets` is **populated**: Uses this array exclusively,
   *   ignoring all boolean widget props
   *
   * **Usage:**
   * ```javascript
   * // New approach (example) - widgets rendered in specified order
   * allowedStudentProgressWidgets = [
   *   STUDENT_PROGRESS_WIDGET.StudentProgressSummaryChartWidget,
   *   STUDENT_PROGRESS_WIDGET.StudentProgressBarWidget,
   *   STUDENT_PROGRESS_WIDGET.StudentProgressWeeklyPerformanceChartWidget
   * ];
   *
   * // Prop-based approach (example) - boolean props via ProgressViewOverride (still works when allowedStudentProgressWidgets is empty)
   * <ProgressViewOverride
   *   summaryWidget={true} // Shows StudentProgressSummaryChartWidget
   *   progressWidget={true} // Shows StudentProgressBarWidget
   *   trophiesWidget={false} // Hides StudentProgressTrophiesWidget
   *   weeklyPerformanceWidget={false} // Hides StudentProgressWeeklyPerformanceChartWidget
   * />
   *
   * // Or via helper service (example)
   * const progressViewOverrideOptions = StudentHelperService.getProgressViewOverrideOptions({
   *   summaryWidget: true,
   *   progressWidget: false,
   *   trophiesWidget: true,
   *   weeklyPerformanceWidget: false
   * });
   * return (
   *   <ProgressViewOverride {...props} {...progressViewOverrideOptions}
   *     // summaryLegend={ProgressViewSummaryLegend}
   *     feedbackIconActiveImg={iconCommentActiveYellow}
   *     leftCol={true} />
   * );
   * ```
   * @type {string[]}
   */
  @observable allowedStudentProgressWidgets = [];

  @observable classroomProgressData = { courses: [], grades: {} };

  @observable selectedContentType = 'all-content';

  /**
   * Trophy tier definitions. When null, uses DEFAULT_STUDENT_PROGRESS_TROPHY_TIERS (gold/silver).
   * Set via satellite index.js or satelliteLayout.json (`studentProgressTrophyTiers`).
   * @type {Array<{ id: string, minScore: number, imageUrl?: string, showBand?: boolean }>|null}
   */
  @observable studentProgressTrophyTiers = null;

  /** When true, trophies only apply to lesson elements. Default false (all scorable types). */
  @observable onlyShowLessonTrophies = false;

  @observable trophyCounts = { gold: 0, silver: 0 };

  @observable meterCounts = { green: 0, yellow: 0, red: 0 };

  @observable coursesVisibility = {};

  // TODO remove
  // @observable isExperimentalReportToggled = false;

  constructor() {
    makeObservable(this);
  }

  // TODO remove
  // @action setIsExperimentalReportToggled = (toggle) => {
  //   this.isExperimentalReportToggled = toggle;
  // }

  @action setAllowStudentProgressNavTab = (allow) => {
    this.allowStudentProgressNavTab = allow;
  }

  @action setAllowedStudentDashboardProgressWidgets = (allowedStudentDashboardProgressWidgets) => {
    if (Array.isArray(allowedStudentDashboardProgressWidgets)) {
      this.allowedStudentDashboardProgressWidgets = allowedStudentDashboardProgressWidgets;
    }
  }

  @action setAllowedStudentProgressWidgets = (allowedStudentProgressWidgets) => {
    if (Array.isArray(allowedStudentProgressWidgets)) {
      this.allowedStudentProgressWidgets = allowedStudentProgressWidgets;
    }
  }

  @action setStudentProgressTrophyTiers = (studentProgressTrophyTiers) => {
    if (Array.isArray(studentProgressTrophyTiers) && studentProgressTrophyTiers.length > 0) {
      this.studentProgressTrophyTiers = studentProgressTrophyTiers;
      this.trophyCounts = this.createEmptyTrophyCounts();
    }
  }

  @action setOnlyShowLessonTrophies = (onlyShowLessonTrophies) => {
    this.onlyShowLessonTrophies = onlyShowLessonTrophies;
  }

  getActiveTrophyTiers = () => {
    const tiers = this.studentProgressTrophyTiers ?? DEFAULT_STUDENT_PROGRESS_TROPHY_TIERS;
    return [...tiers].sort((a, b) => b.minScore - a.minScore);
  }

  getMatchingTrophyTier = (grade) => {
    if (typeof grade !== 'number') {
      return null;
    }
    return this.getActiveTrophyTiers().find((tier) => grade >= tier.minScore) ?? null;
  }

  createEmptyTrophyCounts = () => {
    const counts = {};
    for (const tier of this.getActiveTrophyTiers()) {
      counts[tier.id] = 0;
    }
    return counts;
  }

  @action fetchStudentProgress = async (entityId, classroomId) => {
    let reportId = `${userManager.clientPrefix}_STUDENT_PROGRESS`;
    this.clearAll();
    this.entityId = entityId;

    if (this.isExperimentalReportToggled) {
      reportId += '_R';
    }

    try {
      const response = await Auth.fetch(`${Auth.ecms}/api/viewReportOutput?reportId=${reportId}&entityId=${entityId}&reactErrorType=true`, {
        method: 'GET'
      });

      if (response !== null && response.status === 'SUCCESS') {
        const reportData = response.data[0].contentJson.data;
        this.studentProgressData = reportData;
        this.setClassroom(classroomId);
        // this.classroomProgressData = reportData.classrooms[classroomId];
        // this.initCoursesVisibility(this.classroomProgressData.courses);
        // this.calculateTrophies();
        return true;
      }
    } catch (error) {
      console.error(error);
      // Setting the token in Cookies
      return false;
    }
  }

  @action setClassroom = async (classroomId) => {
    if (this.studentProgressData && this.classroomId != classroomId) {
      this.classroomId = classroomId;
      this.classroomProgressData = this.studentProgressData.classrooms[classroomId];
      if (!this.classroomProgressData) {
        this.classroomProgressData = { courses: [], grades: {} };
      }
      this.initCoursesVisibility();
      this.calculateTrophies();
      this.calculateMeters();
    }
  }

  @action
  clearAll() {
    this.entityId = '';
    this.classroomId = '';
    this.studentProgressData = undefined;
    this.classroomProgressData = { courses: [], grades: {} };
    this.selectedContentType = 'all-content';
    this.coursesVisibility = {};
    this.trophyCounts = this.createEmptyTrophyCounts();
    this.meterCounts = { green: 0, yellow: 0, red: 0 };
  }

  @action
  toggleCourseVisibility(courseId) {
    this.coursesVisibility[courseId] = !this.coursesVisibility[courseId];
    this.coursesVisibility = { ...this.coursesVisibility };
  }

  @action
  setSelectedContentType(contentType) {
    this.selectedContentType = contentType;
    this.calculateTrophies();
    this.calculateMeters();
  }

  initCoursesVisibility() {
    const { courses } = this.classroomProgressData;
    const initialCourseVisibility = {};
    for (const course of courses) {
      initialCourseVisibility[course.courseId] = false;
    }
    this.coursesVisibility = initialCourseVisibility;
  }

  calculateTrophies() {
    const counts = this.createEmptyTrophyCounts();
    const { courses } = this.classroomProgressData;
    const { grades } = this.classroomProgressData;
    for (const course of courses) {
      for (const element of course.courseElements) {
        if (this.selectedContentType === 'Clickbook' && !element.includeWithClickbook) {
          continue;
        } else if (this.selectedContentType === 'ExperTrack' && !element.includeWithExperTrack) {
          continue;
        }

        const gradeObj = grades[element.elementId];
        if (gradeObj) {
          const matchingTier = this.getMatchingTrophyTier(gradeObj.grade);
          if (matchingTier) {
            counts[matchingTier.id]++;
          }
        }
      }
    }

    this.trophyCounts = counts;
  }

  calculateMeters() {
    let greenCount = 0;
    let yellowCount = 0;
    let redCount = 0;
    const { courses } = this.classroomProgressData;
    const { grades } = this.classroomProgressData;
    for (const course of courses) {
      for (const element of course.courseElements) {
        const gradeObj = grades[element.elementId];
        if (gradeObj) {
          const { grade } = gradeObj;
          if (grade >= 80) {
            greenCount++;
          } else if (grade >= 60) {
            yellowCount++;
          } else {
            redCount++;
          }
        }
      }
    }

    this.meterCounts = { green: greenCount, yellow: yellowCount, red: redCount };
  }
}

export default new StudentProgressManager();
